
import React, { useState, useEffect } from "react";
import { User } from "@supabase/supabase-js";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileForm from "@/components/profile/ProfileForm";
import PasswordTab from "@/components/profile/PasswordTab";
import NotificationsTab from "@/components/profile/NotificationsTab";
import UserProfileCard from "@/components/profile/UserProfileCard";
import ProfileLoading from "@/components/profile/ProfileLoading";
import { useProfileForm } from "@/hooks/useProfileForm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface ProfileContentProps {
  user: User | null;
  onOpenConnectionModal?: () => void;
}

const ProfileContent: React.FC<ProfileContentProps> = ({ user, onOpenConnectionModal }) => {
  const [activeTab, setActiveTab] = useState("profile");
  const [hasGoogleProfile, setHasGoogleProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLoadTimeout, setUserLoadTimeout] = useState(false);
  
  // Always call the hook - handle null user inside the hook
  const { register, handleSubmit, errors, onSubmit, isSaving, loadError } = useProfileForm(user);
  
  // Disable showing dev settings button on profile page
  const showDevSettingsLink = false;
  
  // Set a timeout for user loading to prevent indefinite loading states
  useEffect(() => {
    const timeout = setTimeout(() => {
      setUserLoadTimeout(true);
    }, 5000); // 5 second timeout
    
    return () => clearTimeout(timeout);
  }, []);
  
  // Clear timeout when user is loaded
  useEffect(() => {
    if (user) {
      setUserLoadTimeout(false);
    }
  }, [user]);
  
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [user]);
  
  // Show loading state while waiting for user data (but only for a reasonable time)
  if (!user && !userLoadTimeout) {
    return <ProfileLoading reason="loading your profile information" />;
  }
  
  // Show error only if we've waited long enough and still no user
  if (!user && userLoadTimeout) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Unable to load profile information. Please try refreshing the page or signing in again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Account Settings</h1>
        {showDevSettingsLink && (
          <Link to="/developer-settings">
            <Button variant="outline" size="sm">Developer Settings</Button>
          </Link>
        )}
      </div>
      
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <UserProfileCard user={user} />
          
          {null}
        </div>
        
        <div className="md:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <ProfileHeader activeTab={activeTab} setActiveTab={setActiveTab} />
            
            <TabsContent value="profile" className="space-y-6">
              <ProfileForm
                register={register}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                errors={errors}
                isSaving={isSaving}
                error={loadError}
              />
            </TabsContent>

            <TabsContent value="password">
              <PasswordTab />
            </TabsContent>

            <TabsContent value="notifications">
              <NotificationsTab />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ProfileContent;
